import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ContentSection } from '../components/sections/ContentSection';
import { ArrowRight, Loader2 } from 'lucide-react';
import { usePageData } from '../hooks/usePageData';
import { EmbeddedGallery, parseGalleryShortcodes } from '../components/EmbeddedGallery';
import { SEO } from '../components/SEO';
import { sanitizeHTML } from '../utils/sanitize';
import { useCallback } from 'react';

export default function DaycarePreschool() {
  const { pageData, loading, error } = usePageData('daycare-preschool');

  // Parse content for gallery shortcodes
  const contentToParse = pageData?.content || '';
  const { html: parsedContent, galleries } = parseGalleryShortcodes(contentToParse);

  const renderContentWithGalleries = useCallback(() => {
    if (galleries.length === 0) {
      return (
        <div 
          className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-primary-900 prose-p:text-slate-600 prose-a:text-secondary"
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(parsedContent) }}
        />
      );
    }

    const parts = parsedContent.split(/<div data-gallery-placeholder="(\d+)"><\/div>/);
    
    return (
      <div className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-primary-900 prose-p:text-slate-600 prose-a:text-secondary">
        {parts.map((part, index) => {
          if (index % 2 === 0) {
            return part ? <div key={`part-${index}`} dangerouslySetInnerHTML={{ __html: sanitizeHTML(part) }} /> : null;
          } else {
            const galleryIndex = parseInt(part);
            const gallery = galleries[galleryIndex];
            if (gallery) {
              return <EmbeddedGallery key={`gallery-${index}`} category={gallery.category} title="" />;
            }
            return null;
          }
        })}
      </div>
    );
  }, [galleries, parsedContent]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        </div>
      </Layout>
    );
  }

  if (error || !pageData) {
    return (
      <Layout>
        <SEO title="Page Not Found" noIndex={true} />
        <div className="min-h-screen flex items-center justify-center flex-col gap-4">
          <h1 className="font-heading text-2xl font-semibold text-primary-900">Page Not Found</h1>
          <p className="text-slate-500">The page you're looking for doesn't exist.</p>
          <Link to="/" className="text-secondary hover:underline">Return Home</Link>
        </div>
      </Layout>
    );
  }

  const { hero, content, cta, gallerySection, seo } = pageData;
  const seoTitle = seo?.title || hero?.title || 'Daycare & Preschool';
  const seoDescription = seo?.description || hero?.subtitle || '';
  const seoImage = seo?.image || hero?.image || '';

  return (
    <Layout>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        url="/daycare-preschool"
      />
      
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          {hero?.image ? (
            <img 
              src={hero.image}
              alt={hero.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-primary-900" />
          )}
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="daycare-page-title">
            {hero?.title || pageData.title}
          </h1>
          {hero?.subtitle && (
            <p className="text-xl text-slate-200 max-w-2xl mx-auto">
              {hero.subtitle}
            </p>
          )}
        </div>
      </section>

      {/* Main Content */}
      {content && (
        <ContentSection bgColor="bg-white">
          <div className="max-w-4xl mx-auto">
            {renderContentWithGalleries()}
          </div>
        </ContentSection>
      )}

      {/* Gallery Section */}
      {gallerySection?.show && (
        <ContentSection bgColor="bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <EmbeddedGallery 
              category={gallerySection.category || 'all'} 
              title={gallerySection.title || 'Photo Gallery'} 
            />
          </div>
        </ContentSection>
      )}

      {/* CTA */}
      {cta?.show && (
        <ContentSection bgColor={cta.bgColor || "bg-primary-900"} paddingY="py-16">
          <div className="text-center">
            <h2 className={`font-heading text-3xl md:text-4xl font-semibold mb-4 ${cta.bgColor === 'bg-slate-50' ? 'text-primary-900' : 'text-white'}`}>
              {cta.title}
            </h2>
            <p className={`text-lg mb-8 max-w-xl mx-auto ${cta.bgColor === 'bg-slate-50' ? 'text-slate-600' : 'text-slate-300'}`}>
              {cta.description}
            </p>
            {cta.buttonText && cta.buttonLink && (
              <Link
                to={cta.buttonLink}
                className="inline-flex items-center gap-2 bg-secondary text-white px-8 py-4 rounded-full font-semibold hover:bg-secondary-600 transition-all hover:shadow-lg group"
                data-testid="daycare-cta-button"
              >
                {cta.buttonText}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            )}
          </div>
        </ContentSection>
      )}
    </Layout>
  );
}
